import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { mapLabeledCode } from '../../../lib/labeledCode';
import { normalizeLocale } from '../../../lib/locale';
import type { Contract } from '../contractTypes';
import EmployeeEndpoints from '../../employees/api/employeeEndpoints';

const str = (v: unknown): string | undefined => {
  const s = v != null ? String(v).trim() : '';
  return s !== '' ? s : undefined;
};

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return isNaN(n) ? undefined : n;
};

/**
 * Maps a backend DTO to the frontend Contract shape.
 */
function mapContractRow(raw: Record<string, unknown>): Contract {
  return {
    id: num(raw.id),
    hc_uuid_key: str(raw.hc_uuid_key) ?? '',
    hc_uuid_key_staff: str(raw.hc_uuid_key_staff) ?? '',

    contract_creation_type: mapLabeledCode(raw.typecreation),
    contract_type:          mapLabeledCode(raw.typecontrat),
    initial_start_date:     str(raw.initialstartdate),
    contract_start_date:    str(raw.datedeb),
    contract_end_date:      str(raw.datefin),
    work_location:          str(raw.lieuaffectation),
    trial_period:           mapLabeledCode(raw.periodeessai),
    trial_period_end_date:  str(raw.datefinpessai),
    contract_number:        str(raw.numero),
    contract_current:       mapLabeledCode(raw.contratencours),

    seniority_months: num(raw.anciennete),

    pay_plan:              mapLabeledCode(raw.planpaye),
    salary_scale:          mapLabeledCode(raw.grille),
    level:                 str(raw.niveau),
    base_salary:           num(raw.salairebasewithoutseniority),
    salary_with_seniority: num(raw.salairebase),
    activity_rate:         num(raw.tauxactivite),
    currency:              mapLabeledCode(raw.devise),
    employer:              mapLabeledCode(raw.employeur),
    country:               mapLabeledCode(raw.pays),

    leave_type:              str(raw.leave_type),
    leave_accrual_per_month: num(raw.total_conge),
    leave_balance:           num(raw.soldeprecedent),
    leave_balance_date:      str(raw.datesoldeprecedent),

    line_manager:       mapLabeledCode(raw.line_manager),
    functional_manager: mapLabeledCode(raw.func_manager),

    job_function:   mapLabeledCode(raw.fonction),
    function_grid:  mapLabeledCode(raw.entetefonction),
    category:       mapLabeledCode(raw.categorie),
    position_grade: mapLabeledCode(raw.gradeposte),

    contract_file: str(raw.fichiercontrat),
    job_profile:   mapLabeledCode(raw.profilposte),
    profile_file:  str(raw.fichierprofil),

    accounting_code:    mapLabeledCode(raw.compte),
    axe_analytique:    mapLabeledCode(raw.axesaga),
    analytical_line:    mapLabeledCode(raw.ligneaxesaga),
    axe_analytique_2:  mapLabeledCode(raw.axesaga2),
    analytical_line_2:  mapLabeledCode(raw.ligneaxesaga2),
    project:           mapLabeledCode(raw.projet),
    sub_project:       mapLabeledCode(raw.axeanalytique),
    funding_contract:  mapLabeledCode(raw.contratsaga),
    funding_line:      mapLabeledCode(raw.lignefinsaga),
    third_party_code:  str(raw.tiers),

    comment: str(raw.commentcontrat),

    termination_type:     mapLabeledCode(raw.typefincontrat),
    termination_reason:   str(raw.motiffincontrat),
    final_settlement:     mapLabeledCode(raw.soldetoutcompte),
    indemnity_start_date: str(raw.startdate),
  };
}

export function ContractDetailApiFactory(client: FetchClient, lang: string) {
  const headers = { 'Accept-Language': normalizeLocale(lang) };

  return {
    /**
     * Fetches the most recent active contract for a given employee.
     * GET /employees/:id/contracts/current
     */
    getCurrentByEmployeeId: async (
      employeeId: string,
      opts?: RequestOptions,
    ): Promise<Contract | null> => {
      const raw = await client.get<Record<string, unknown>>(
        EmployeeEndpoints.CONTRACTS_CURRENT(employeeId),
        { ...opts, headers: { ...opts?.headers, ...headers } },
      );
      if (!raw) return null;
      return mapContractRow(raw);
    },

    /**
     * Fetches a specific contract by hc_uuid_key.
     * GET /employees/:id/contracts/:contractId
     */
    getById: async (
      employeeId: string,
      contractId: string,
      opts?: RequestOptions,
    ): Promise<Contract | null> => {
      const raw = await client.get<Record<string, unknown>>(
        EmployeeEndpoints.CONTRACT_BY_UUID_KEY(employeeId, contractId),
        { ...opts, headers: { ...opts?.headers, ...headers } },
      );
      if (!raw) return null;
      return mapContractRow(raw);
    },
  };
}
